import { useProduct } from "../../lib/tenant"
import type { ILogoIconProps, ILogoProps } from "./interfaces"
import classes from "./styles.module.css"

const PRODUCT_NAMES = {
  lexicanon: "Lexicanon",
  voxcanon: "Voxcanon",
}

// Lexicanon — geometric mark. Gradient stops read theme tokens (teal→indigo
// by default; Bordeaux recolours them to wine via lexicanon-tokens.css).
function LexicanonIcon({ size = 32 }: ILogoIconProps): React.JSX.Element {
  const gradId = `lexi-grad-${size}`
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" role="img" aria-label="Lexicanon logo">
      <title>Lexicanon logo</title>
      <defs>
        <linearGradient id={gradId} x1="0" y1="20" x2="80" y2="60" gradientUnits="userSpaceOnUse">
          <stop offset="0%" className={classes.lexGradFrom} />
          <stop offset="100%" className={classes.lexGradTo} />
        </linearGradient>
      </defs>
      <path d="M40 16L8 40l12 8 20-16 20 16 12-8L40 16z" fill={`url(#${gradId})`} opacity="0.15" />
      <path
        d="M40 16L8 40l12 8 20-16 20 16 12-8L40 16z"
        stroke={`url(#${gradId})`}
        strokeWidth="2.5"
        fill="none"
        strokeLinejoin="round"
      />
      <path d="M20 52h40" stroke={`url(#${gradId})`} strokeWidth="3" strokeLinecap="round" />
    </svg>
  )
}

// Voxcanon — Axiom the crystalline dragon mark
// Wings + crystalline diamond body + golden eye + horn
function VoxcanonIcon({ size = 32 }: ILogoIconProps): React.JSX.Element {
  const gradId = `vox-grad-${size}`
  const goldId = `vox-gold-${size}`
  const glowId = `vox-glow-${size}`
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" role="img" aria-label="Voxcanon logo">
      <title>Voxcanon — Axiom</title>
      <defs>
        <linearGradient id={gradId} x1="10" y1="5" x2="70" y2="75" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="55%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
        <linearGradient id={goldId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#a855f7" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Soft glow behind crystal */}
      <circle cx="40" cy="42" r="20" fill={`url(#${glowId})`} />

      {/* Left wing */}
      <path d="M6,62 C10,50 18,34 30,24 L36,42 Z" fill={`url(#${gradId})`} opacity="0.35" />
      <path d="M6,62 C10,50 18,34 30,24 L36,42 Z" stroke={`url(#${gradId})`} strokeWidth="1" fill="none" />

      {/* Right wing */}
      <path d="M74,62 C70,50 62,34 50,24 L44,42 Z" fill={`url(#${gradId})`} opacity="0.35" />
      <path d="M74,62 C70,50 62,34 50,24 L44,42 Z" stroke={`url(#${gradId})`} strokeWidth="1" fill="none" />

      {/* Crystal body — diamond */}
      <path d="M40,18 L56,42 L40,64 L24,42 Z" fill={`url(#${gradId})`} opacity="0.18" />
      <path
        d="M40,18 L56,42 L40,64 L24,42 Z"
        stroke={`url(#${gradId})`}
        strokeWidth="1.8"
        fill="none"
        strokeLinejoin="round"
      />

      {/* Crystal facets — inner cross-sections */}
      <path
        d="M40,18 L56,42 M40,18 L24,42 M24,42 L40,64 M56,42 L40,64"
        stroke={`url(#${gradId})`}
        strokeWidth="0.8"
        opacity="0.4"
      />
      <path d="M24,42 L56,42" stroke={`url(#${gradId})`} strokeWidth="0.8" opacity="0.35" />

      {/* Horn — crystal spike at top */}
      <path d="M36.5,18 L40,6 L43.5,18 Z" fill={`url(#${gradId})`} opacity="0.85" />
      <path d="M36.5,18 L40,6 L43.5,18" stroke={`url(#${gradId})`} strokeWidth="1" strokeLinejoin="round" fill="none" />

      {/* Golden eye — Axiom's orb */}
      <circle cx="40" cy="40" r="5.5" fill={`url(#${goldId})`} opacity="0.9" />
      <circle cx="40" cy="40" r="2.5" fill="#fef3c7" />
      <circle cx="38.5" cy="38.5" r="1" fill="rgba(255,255,255,0.6)" />
    </svg>
  )
}

export function LogoIcon({ size = 32 }: ILogoIconProps): React.JSX.Element {
  const product = useProduct()
  if (product === "voxcanon") return <VoxcanonIcon size={size} />
  return <LexicanonIcon size={size} />
}

export function Logo({ size = 28, showCompany, iconless = false }: ILogoProps): React.JSX.Element {
  const product = useProduct()
  const showIcon = !iconless

  return (
    <span className={classes.wrapper} data-product={product} data-iconless={iconless || undefined}>
      {showIcon && <LogoIcon size={size} />}
      <span className={classes.productName} data-product={product} data-iconless={iconless || undefined}>
        {PRODUCT_NAMES[product]}
      </span>
      {showCompany && (
        <span className={classes.companyName} data-product={product}>
          — {showCompany}
        </span>
      )}
    </span>
  )
}
